import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerMembership } from '../../../../../../../../../../models/membership';
import { getProratedRemainingAmount } from '../../../../../../../../../../utils-models';
import { PaymentType } from '../../../../../../../../../../models/payment-enums';
import {
  getRefundBlockedByPaymentTypeMessage
} from '../../../../../../../../../../models/payment';
import * as moment from 'moment-timezone';


enum REFUND_POLICY {
  noRefund,
  refundLastPayment,
  refundCredit,
}


@Component({
  selector: 'app-edit-non-recurring-customer-membership-cancel',
  templateUrl: './edit-non-recurring-customer-membership-cancel.component.html',
  styleUrls: ['./edit-non-recurring-customer-membership-cancel.component.css']
})
export class EditNonRecurringCustomerMembershipCancelComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public refundPolicy: REFUND_POLICY = REFUND_POLICY.noRefund;
  public refundCreditAmount = 0;
  public refundLastPayment = false;
  public cancellationEmailText = '';
  public reason: string = null;

  // Set when the last invoice was paid with a tender Capitan cannot refund (a
  // POS or offline payment), which disables the refund option.
  public refundBlockedPaymentType: PaymentType | null = null;

  public errorMessage = '';

  public REFUND_POLICY = REFUND_POLICY;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    if (!this.membership.last_invoice_amount) {
      if (this.refundPolicy === REFUND_POLICY.refundLastPayment) {
        this.refundPolicy = REFUND_POLICY.noRefund;
      }
    }

    this.refundBlockedPaymentType =
      this.membership.last_invoice_refund_blocking_payment_type;

    // Belt and braces alongside the disabled radio: the server rejects a refund
    // of this tender outright, which abandons the whole cancellation.
    if (this.refundBlockedPaymentType
        && (this.refundPolicy === REFUND_POLICY.refundLastPayment)) {
      this.refundPolicy = REFUND_POLICY.noRefund;
    }

    // Reload the org config, in case the membership cancellation reason policy has changed.
    this.orgConfigService.loadOrganizationConfig();

    const billingPeriodAmount = parseFloat(this.membership.total_paid_to_date);
    const todayStart = moment().startOf('day');
    let billingPeriodStart = moment(this.membership.start_date);
    if (!!this.membership.last_invoice_date && (this.membership.last_invoice_date > this.membership.start_date)) {
      billingPeriodStart = moment(this.membership.last_invoice_date);
    }
    const billingPeriodEnd = moment(this.membership.end_date);
    this.refundCreditAmount = getProratedRemainingAmount(
      billingPeriodAmount,
      billingPeriodStart,
      billingPeriodEnd,
      todayStart
    );
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  /**
   * The warning shown when Capitan cannot refund this membership's last
   * payment, explaining where the refund has to be issued instead. Empty when
   * the payment is refundable from here.
   */
  get refundBlockedMessage(): string {
    if (!this.refundBlockedPaymentType) {
      return '';
    }

    let message = getRefundBlockedByPaymentTypeMessage(
      this.refundBlockedPaymentType
    );

    if (this.refundCreditAmount > 0) {
      message += ' You can still issue prorated account credit below.';
    }

    return message;
  }

  private handleSave() {
    const orgConfig = this.orgConfigService.getOrganizationConfig();
    if (orgConfig.require_membership_cancellation_reason && (this.reason === null)) {
      this.errorMessage = 'Please select a reason for the cancellation.';
      return;
    }

    this.loadingEvent.emit(true);

    this.errorMessage = '';

    this.customerService
      .cancelMembershipImmediately(
        this.membership,
        this.cancellationEmailText,
        this.refundPolicy === REFUND_POLICY.refundLastPayment,
        (this.refundPolicy === REFUND_POLICY.refundCredit ? this.refundCreditAmount : 0),
        this.reason
      )
      .then(() => {
        this.savedCallback('Membership cancelled successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to cancel this membership.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

}
